import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, Fragment } from 'react';
import { fetchStudies, deleteStudy, updateStudy } from '../services/studyService';
import { diagnosisLabel } from '../constants/diseaseOptions';
import { formatBackupCrcs } from '../services/backupCrc';
import { formatDateToDDMMMYYYY } from '../utils/dateUtils';
import EditStudyModal from './EditStudyModal';
import StudyCard from './study/StudyCard';
import PotentialPatientsCard from './study/PotentialPatientsCard';
import ScreeningVisitCard from './study/ScreeningVisitCard';
import PendingReportUpdatesCard from './study/PendingReportUpdatesCard';
import { useTenant } from '../context/TenantContext';
import { effectiveRole } from '../services/membershipService';
import { STUDY_SCREENING_ENABLED, PROTOCOL_COMPARE_ENABLED, UI_REVAMP_ENABLED } from '../config/featureFlags';
import ProtocolCompareButton from './study/ProtocolCompareButton';
import StudyClosePatientsModal from './StudyClosePatientsModal';
import { loadStudyClosePlans } from '../services/studyCloseService';
import { isClosedStudy, REFERRALS_STATUS } from '../services/enrollmentRules';

// UI extras
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Building2,
  FileText,
  ClipboardList,
  IdCard,
  UserCircle2,
  Target,
  CalendarDays,
  CheckCircle2,
  AlertCircle,
  Activity,
  HeartPulse,
  Loader2,
  Sparkles,
  ChevronRight,
} from 'lucide-react';

const STATUS_STYLES = {
  ACTIVE: {
    chip: 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30',
    dot: 'bg-emerald-400 shadow-[0_0_10px_2px_rgba(52,211,153,0.7)]',
    bar: 'from-emerald-400 to-teal-400',
  },
  'START-UP': {
    chip: 'bg-amber-400/15 text-amber-300 border-amber-400/30',
    dot: 'bg-amber-400 shadow-[0_0_10px_2px_rgba(251,191,36,0.7)]',
    bar: 'from-amber-400 to-orange-400',
  },
  'ACTIVE/NR': {
    chip: 'bg-sky-400/15 text-sky-300 border-sky-400/30',
    dot: 'bg-sky-400 shadow-[0_0_10px_2px_rgba(56,189,248,0.7)]',
    bar: 'from-sky-400 to-blue-400',
  },
  CLOSED: {
    chip: 'bg-slate-400/15 text-slate-300 border-slate-400/30',
    dot: 'bg-slate-400',
    bar: 'from-slate-400 to-slate-500',
  },
  'CLOSED/CLOSING': {
    chip: 'bg-slate-400/15 text-slate-300 border-slate-400/30',
    dot: 'bg-slate-400',
    bar: 'from-slate-400 to-slate-500',
  },
  DEFAULT: {
    chip: 'bg-purple-400/15 text-purple-300 border-purple-400/30',
    dot: 'bg-purple-400 shadow-[0_0_10px_2px_rgba(167,139,250,0.7)]',
    bar: 'from-purple-400 to-indigo-400',
  },
};

const statusKey = (s) => (s || '').toString().trim().toUpperCase();

const StudyDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [study, setStudy] = useState(null);
  const [editingStudy, setEditingStudy] = useState(null);

  // Uploading a protocol, confirming the screening checklist and spending an
  // AI call on the criteria are admin actions; coordinators see the results.
  const { access, tenantId, siteId } = useTenant();
  const role = effectiveRole(access, tenantId, siteId);
  const isAdmin = role === 'admin' || role === 'owner' || Boolean(access?.isOwner);

  // The cards save their own fields; fold the saved value into the study we
  // already hold rather than re-fetching the whole collection.
  const patchStudy = (patch) => setStudy((prev) => (prev ? { ...prev, ...patch } : prev));

  // Closed study → offer its enrolled patients back to referrals. The pop-up
  // opens right after a save closes it (here, or from /edit/:id via router
  // state); if the admin skips it, the banner below keeps offering it until
  // every enrolled patient has been updated.
  const location = useLocation();
  const [closeModalOpen, setCloseModalOpen] = useState(Boolean(location.state?.justClosed));
  const [pendingCloseCount, setPendingCloseCount] = useState(0);
  const [closeCheck, setCloseCheck] = useState(0);
  const studyIsClosed = isClosedStudy(study);
  // UI revamp: opened from the home page's "Needs your attention", Back goes home.
  const [fromHome] = useState(() => UI_REVAMP_ENABLED && location.state?.from === 'home');
  // Consume the /edit/:id hand-off once, so a page refresh doesn't reopen it.
  useEffect(() => {
    if (location.state?.justClosed) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!study || !studyIsClosed || !isAdmin) {
      setPendingCloseCount(0);
      return undefined;
    }
    let cancelled = false;
    loadStudyClosePlans(study)
      .then(({ plans }) => !cancelled && setPendingCloseCount(plans.length))
      .catch(() => !cancelled && setPendingCloseCount(0));
    return () => {
      cancelled = true;
    };
    // Re-check when the study loads/changes status or the pop-up closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study?.id, studyIsClosed, isAdmin, closeCheck]);



  useEffect(() => {
    const loadStudy = async () => {
      const data = await fetchStudies();
      const found = data.find((s) => s.id === id);
      setStudy(found);
    };
    loadStudy();
  }, [id]);

  // Set by the Screening Visit card while its checklist has unconfirmed edits,
  // so leaving via this page's Back buttons warns instead of losing them.
  const screeningDirtyRef = useRef(false);
  const backToDashboard = () => {
    if (
      screeningDirtyRef.current &&
      !window.confirm(
        "You have changes to the screening checklist that haven't been confirmed. Leave and lose them?"
      )
    )
      return;
    navigate(fromHome ? '/home' : '/dashboard');
  };

  const handleDelete = async () => {
    const ok = window.confirm('Are you sure you want to delete this study?');
    if (ok) {
      await deleteStudy(id);
      navigate('/');
    }
  };

  const handleSave = async (updatedStudy) => {
    const wasClosed = isClosedStudy(study);
    await updateStudy(id, updatedStudy);
    if (!wasClosed && isClosedStudy(updatedStudy)) setCloseModalOpen(true);

// ✅ re-fetch instead of trusting local state
const data = await fetchStudies();
const found = data.find((s) => s.id === id);
setStudy(found);
    setEditingStudy(null);
  };

  if (!study) {
    return (
      <div className="relative min-h-screen w-full text-slate-100 font-[Manrope] flex items-center justify-center">
        <PageBackground />
        <div className="relative flex flex-col items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl blur-md opacity-70" />
            <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400 font-medium">
            Loading study…
          </p>
        </div>
      </div>
    );
  }

  const milestoneKeys = [
    'CDA','FEASIBILITY','SQV_DATE','IRB_SUBMISSION','SIV','ISF_SETUP','1572','FDF',
    'CREDENTIALS','PSP','IB_AOR','SCF','TL','DOA','SIV_FOLLOWUP','SDA','SRP','COI','GREENLIGHT','SSC','COV'
  ];

  const completedMilestones = milestoneKeys.filter((k) => {
    const d = formatDateToDDMMMYYYY(study[k]);
    return d && d !== 'Invalid Date' && d !== 'NaN';
  }).length;

  const style = STATUS_STYLES[statusKey(study.STATUS)] || STATUS_STYLES.DEFAULT;
  const goal = Number(study.GOAL) || 0;
  const enrolled = Number(study.ENROLLED) || 0;
  const progress = goal > 0 ? Math.min(100, Math.round((enrolled / goal) * 100)) : 0;

  return (
    <div className="relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="relative px-3 sm:px-4 lg:px-6 py-4 max-w-7xl mx-auto">
        {/* Breadcrumb / Back */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={backToDashboard}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {fromHome ? 'Home' : 'Dashboard'}
          </button>
          <ChevronRight className="w-3 h-3 text-slate-500" />
          <span className="text-xs text-slate-400">Study Details</span>
          {study?.STUDY_ID && (
            <>
              <ChevronRight className="w-3 h-3 text-slate-500" />
              <span className="text-[11px] font-mono uppercase tracking-wider text-purple-300/80">
                {study.STUDY_ID}
              </span>
            </>
          )}
        </div>

        {/* Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_20px_60px_-20px_rgba(124,58,237,0.45)]"
        >
          <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${style.bar} opacity-80`} />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10 pointer-events-none" />
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-purple-600/25 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />

          <div className="relative p-4 sm:p-5">
            <div className="flex flex-col lg:flex-row lg:items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <StatusBadge status={study?.STATUS} />
                  {study?.DISEASE && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/[0.04] border border-white/10 text-slate-300">
                      <HeartPulse className="w-2.5 h-2.5 text-purple-300" />
                      {diagnosisLabel(study.DISEASE)}
                    </span>
                  )}
                  {study?.SPONSOR && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/[0.04] border border-white/10 text-slate-300">
                      <FileText className="w-2.5 h-2.5 text-purple-300" />
                      {study.SPONSOR}
                    </span>
                  )}
                </div>

                <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white leading-tight">
                  {study?.NAME || 'Study'}
                </h1>
                <p className="text-[11px] uppercase tracking-[0.22em] text-purple-300/80 mt-0.5 font-semibold">
                  Study Details
                </p>

                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-300">
                  <span className="inline-flex items-center gap-1.5">
                    <IdCard className="w-3.5 h-3.5 text-purple-300" />
                    <span className="text-slate-400">ID</span>
                    <span className="font-mono text-slate-100">{study?.STUDY_ID || '—'}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-purple-300" />
                    <span className="text-slate-400">Site</span>
                    <span className="font-mono text-slate-100">{study?.SITE_ID || '—'}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <UserCircle2 className="w-3.5 h-3.5 text-purple-300" />
                    <span className="text-slate-400">Lead CRC</span>
                    <span className="text-slate-100">{study?.LEAD_CRC || '—'}</span>
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 lg:shrink-0">
                <button
                  onClick={() => setEditingStudy(study)}
                  className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Pencil className="w-3.5 h-3.5 relative" />
                  <span className="relative">Edit Study</span>
                </button>
                <button
                  onClick={backToDashboard}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs font-medium text-slate-200 hover:text-white transition"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back
                </button>
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 hover:border-rose-400/50 text-xs font-medium text-rose-200 hover:text-white transition"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>

            {/* Enrollment progress strip */}
            {goal > 0 && (
              <div className="mt-4 pt-3 border-t border-white/5">
                <div className="flex items-center justify-between text-[10px] mb-1.5">
                  <span className="uppercase tracking-[0.22em] text-slate-400 font-semibold">
                    Enrollment Progress
                  </span>
                  <span className="text-slate-200 font-semibold">
                    {enrolled} / {goal} · {progress}%
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${style.bar} shadow-[0_0_10px_rgba(139,92,246,0.5)] transition-all duration-500`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Facts */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={container}
          className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-2.5"
        >
          <StatCard
            icon={<Target className="w-4 h-4 text-white/80" />}
            label="Enrollment Goal"
            value={safeNum(study.GOAL)}
            accent="from-purple-500/20 to-indigo-500/20"
          />
          <StatCard
            icon={<CheckCircle2 className="w-4 h-4 text-white/80" />}
            label="Enrolled"
            value={safeNum(study.ENROLLED)}
            accent="from-emerald-500/20 to-teal-500/20"
            valueClass="text-emerald-300"
          />
          <StatCard
            icon={<AlertCircle className="w-4 h-4 text-white/80" />}
            label="Screen Failures"
            value={safeNum(study.SCREEN_FAILURES)}
            accent="from-rose-500/20 to-pink-500/20"
            valueClass="text-rose-300"
          />
          <StatCard
            icon={<CalendarDays className="w-4 h-4 text-white/80" />}
            label="Milestones"
            value={`${completedMilestones}/${milestoneKeys.length}`}
            accent="from-amber-500/20 to-orange-500/20"
            valueClass="text-amber-300"
          />
        </motion.div>

        {/* Coordinators' weekly-report numbers waiting for an admin's OK
            (adds to Enrolled / Screen Failures only when approved) */}
        {isAdmin && <PendingReportUpdatesCard study={study} onStudyPatched={patchStudy} />}

        {/* Core Details */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3"
        >
          <Card
            title="Core Details"
            subtitle="Key identifiers and configuration"
            icon={<Activity className="w-3.5 h-3.5 text-purple-300" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2.5">
              <DetailRow icon={<IdCard className="w-3.5 h-3.5" />} label="STUDY ID" value={study.STUDY_ID} mono />
              <DetailRow icon={<ClipboardList className="w-3.5 h-3.5" />} label="NAME" value={study.NAME} />
              <DetailRow icon={<FileText className="w-3.5 h-3.5" />} label="SPONSOR" value={study.SPONSOR} />
              <DetailRow icon={<Building2 className="w-3.5 h-3.5" />} label="SITE ID" value={study.SITE_ID} mono />
              <DetailRow icon={<UserCircle2 className="w-3.5 h-3.5" />} label="LEAD CRC" value={study.LEAD_CRC} />
              <DetailRow icon={<UserCircle2 className="w-3.5 h-3.5" />} label="BACKUP CRC" value={formatBackupCrcs(study.BACKUP_CRC)} />
              <DetailRow icon={<CalendarDays className="w-3.5 h-3.5" />} label="SUBJECT PAYMENT METHOD" value={study.SUBJECT_PAYMENT_METHOD} />
              <DetailRow icon={<FileText className="w-3.5 h-3.5" />} label="BUDGET ACCEPTED" value={stringifyYesNo(study.BUDGET_ACCEPTED)} />
              <DetailRow icon={<FileText className="w-3.5 h-3.5" />} label="TRAVEL" value={study.TRAVEL} />
              <DetailRow icon={<CalendarDays className="w-3.5 h-3.5" />} label="SSC" value={formatDateToDDMMMYYYY(study.SSC)} />
              <DetailRow
                icon={<ClipboardList className="w-3.5 h-3.5" />}
                label="Paid # of SF"
                value={study["Paid # of SF"]}
              />
              <DetailRow icon={<FileText className="w-3.5 h-3.5" />} label="CTA" value={stringifyYesNo(study.CTA)} />
            </div>
          </Card>
        </motion.section>

        {/* Milestones Grid */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3"
        >
          <Card
            title="Milestones"
            subtitle={`Regulatory & startup timeline · ${completedMilestones} of ${milestoneKeys.length} completed`}
            icon={<CalendarDays className="w-3.5 h-3.5 text-purple-300" />}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {milestoneKeys.map((k) => (
                <MilestoneItem key={k} label={k} dateStr={formatDateToDDMMMYYYY(study[k])} />
              ))}
            </div>
          </Card>
        </motion.section>

        {/* Criteria */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3"
        >
          <Card
            title="Eligibility Criteria"
            subtitle="Inclusion & Exclusion"
            icon={<ClipboardList className="w-3.5 h-3.5 text-purple-300" />}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <details className="group rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-3 open:bg-white/[0.05] transition">
                <summary className="cursor-pointer flex items-center gap-2 text-xs font-semibold text-white list-none">
                  <span className="inline-flex w-5 h-5 rounded-md bg-emerald-500/15 border border-emerald-400/30 items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-emerald-300" />
                  </span>
                  Inclusion Criteria
                  <ChevronRight className="ml-auto w-3.5 h-3.5 text-slate-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-2 whitespace-pre-wrap text-xs text-slate-300 leading-relaxed">
                  {study.INCLUSION || '—'}
                </p>
              </details>
              <details className="group rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-3 open:bg-white/[0.05] transition">
                <summary className="cursor-pointer flex items-center gap-2 text-xs font-semibold text-white list-none">
                  <span className="inline-flex w-5 h-5 rounded-md bg-rose-500/15 border border-rose-400/30 items-center justify-center">
                    <AlertCircle className="w-3 h-3 text-rose-300" />
                  </span>
                  Exclusion Criteria
                  <ChevronRight className="ml-auto w-3.5 h-3.5 text-slate-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-2 whitespace-pre-wrap text-xs text-slate-300 leading-relaxed">
                  {study.EXCLUSION || '—'}
                </p>
              </details>
            </div>
          </Card>
        </motion.section>

        {/* Closed study with enrolled patients not yet returned to referrals */}
        {isAdmin && studyIsClosed && pendingCloseCount > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3"
          >
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-teal-400/30 bg-teal-400/10 backdrop-blur-xl px-4 py-3">
              <p className="flex-1 text-xs text-teal-100">
                This study is closed. {pendingCloseCount} enrolled patient{pendingCloseCount === 1 ? '' : 's'} still
                {pendingCloseCount === 1 ? ' needs' : ' need'} to be moved back to {REFERRALS_STATUS} (or marked as
                still enrolled elsewhere).
              </p>
              <button
                type="button"
                onClick={() => setCloseModalOpen(true)}
                className="enroll-solid-btn inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 ring-1 ring-white/20 text-xs font-semibold text-white"
              >
                Review patients
              </button>
            </div>
          </motion.section>
        )}

        {/* Potential Patients — criteria -> RegX query -> shortlist / PDF */}
        {STUDY_SCREENING_ENABLED && (
          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3"
          >
            <PotentialPatientsCard study={study} canEdit={isAdmin} onStudyPatched={patchStudy} />
          </motion.section>
        )}

        {/* Screening Visit — protocol upload -> SoA -> "Needs X" checklist */}
        {(STUDY_SCREENING_ENABLED || study.screeningVisit) && (
          <motion.section
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3"
          >
            <ScreeningVisitCard
              study={study}
              canEdit={isAdmin && STUDY_SCREENING_ENABLED}
              onStudyPatched={patchStudy}
              onDirtyChange={(d) => {
                screeningDirtyRef.current = d;
              }}
            />
          </motion.section>
        )}

        {/* Protocol Compare — admins: compare a sponsor's new protocol (flag) */}
        {PROTOCOL_COMPARE_ENABLED && isAdmin && <ProtocolCompareButton study={study} />}

        {/* Comments */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-3"
        >
          <Card
            title="Comments"
            subtitle="Study notes"
            icon={<FileText className="w-3.5 h-3.5 text-purple-300" />}
          >
            <div className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-3">
              <p className="whitespace-pre-wrap text-xs text-slate-300 leading-relaxed">
                {study["Comments"] || "—"}
              </p>
            </div>
          </Card>
        </motion.section>

        {/* Sticky Action Bar */}
        <div className="h-16" />
        <div className="sticky bottom-3 flex w-full justify-center pointer-events-none">
          <div className="inline-flex gap-1.5 rounded-2xl bg-slate-950/70 backdrop-blur-2xl border border-white/10 shadow-[0_20px_60px_-15px_rgba(124,58,237,0.5)] p-1.5 pointer-events-auto">
            <button
              onClick={() => setEditingStudy(study)}
              className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Pencil className="w-3.5 h-3.5 relative" />
              <span className="relative">Edit</span>
            </button>
            <button
              onClick={backToDashboard}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs font-medium text-slate-200 hover:text-white transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-rose-500/15 border border-rose-400/30 hover:bg-rose-500/25 hover:border-rose-400/50 text-xs font-medium text-rose-200 hover:text-white transition"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        </div>

        {/* Edit Modal */}
        <AnimatePresence>
          {editingStudy && (
            <EditStudyModal
              study={editingStudy}
              onSave={handleSave}
              onClose={() => setEditingStudy(null)}
            />
          )}
        </AnimatePresence>

        <StudyClosePatientsModal
          open={closeModalOpen && isAdmin && studyIsClosed}
          study={study}
          onClose={() => {
            setCloseModalOpen(false);
            setCloseCheck((n) => n + 1);
          }}
        />
      </div>
    </div>
  );
};

/* ---------------------------- UI Subcomponents --------------------------- */

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05 }
  }
};

const item = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0 }
};

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
    <div className="absolute bottom-0 left-1/3 w-[480px] h-[480px] rounded-full bg-fuchsia-600/15 blur-[140px]" />
  </div>
);

const Card = StudyCard;

const DetailRow = ({ label, value, icon, mono }) => (
  <div className="flex items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-white/[0.02] transition">
    <div className="mt-0.5 text-purple-300/80 shrink-0">{icon}</div>
    <div className="min-w-0 flex-1">
      <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold leading-none mb-0.5">
        {label}
      </div>
      <div
        className={`text-xs ${mono ? 'font-mono' : ''} text-slate-100 break-words leading-snug`}
      >
        {value || '—'}
      </div>
    </div>
  </div>
);

const StatCard = ({ icon, label, value, accent = 'from-purple-500/20 to-indigo-500/20', valueClass = 'text-white' }) => (
  <motion.div
    variants={item}
    className="relative overflow-hidden rounded-2xl border border-white/10 hover:border-purple-300/30 bg-white/[0.03] backdrop-blur-xl px-3.5 py-2.5 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(139,92,246,0.4)] group"
  >
    <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-60 pointer-events-none`} />
    <div className="relative flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-300/80 font-medium">
          {label}
        </div>
        <div className={`text-2xl font-bold tracking-tight leading-tight ${valueClass}`}>
          {value}
        </div>
      </div>
      <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-105 transition">
        {icon}
      </div>
    </div>
  </motion.div>
);

const MilestoneItem = ({ label, dateStr }) => {
  const isDone = Boolean(dateStr && dateStr !== 'Invalid Date' && dateStr !== 'NaN');
  return (
    <motion.div
      variants={item}
      className={`relative overflow-hidden rounded-xl border ${
        isDone ? 'border-emerald-400/20' : 'border-white/10'
      } bg-white/[0.03] backdrop-blur-xl p-2.5 flex items-center justify-between gap-2 hover:bg-white/[0.05] transition group`}
    >
      <span
        className={`absolute left-0 top-0 bottom-0 w-[2px] ${
          isDone
            ? 'bg-gradient-to-b from-emerald-400 to-teal-400 shadow-[0_0_10px_rgba(52,211,153,0.5)]'
            : 'bg-white/5'
        }`}
      />
      <div className="min-w-0 flex-1 pl-1.5">
        <div className="text-xs font-semibold text-white truncate leading-tight">{label}</div>
        <div className={`text-[10px] uppercase tracking-wider font-medium leading-tight ${isDone ? 'text-emerald-300/80' : 'text-slate-500'}`}>
          {isDone ? 'Completed' : 'Pending'}
        </div>
      </div>
      <span
        className={`text-[10px] font-medium px-2 py-1 rounded-md border whitespace-nowrap ${
          isDone
            ? 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30'
            : 'bg-amber-400/10 text-amber-300/80 border-amber-400/20'
        }`}
      >
        {isDone ? dateStr : '—'}
      </span>
    </motion.div>
  );
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[statusKey(status)] || STATUS_STYLES.DEFAULT;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide border backdrop-blur-md ${s.chip}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {status || 'STATUS —'}
    </span>
  );
};

/* --------------------------------- Utils -------------------------------- */

function stringifyYesNo(v) {
  if (v === true || v === 'true' || v === 'Yes') return 'Yes';
  if (v === false || v === 'false' || v === 'No') return 'No';
  return v || '—';
}

function safeNum(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n : String(v);
}

export default StudyDetailsPage;
